/**
 * Operator-facing catalog of NextCompli product features.
 * Keys match apps/gateway/feature_catalog.py and pricing tier entitlements.
 */

export type FeatureCatalogEntry = {
  key: string;
  name: string;
  description: string;
  category: string;
  permission: string;
};

export const FEATURE_CATALOG: FeatureCatalogEntry[] = [
  {
    key: 'usage-based-pricing-plans',
    name: 'Usage-based pricing plans',
    description: 'See and manage plan pricing that scales with use.',
    category: 'Platform',
    permission: 'feature.usage-based-pricing-plans.use',
  },
  {
    key: 'ofac-compliance',
    name: 'OFAC Compliance',
    description: 'Named-party sanctions screening for customers, vendors, payees, and hires.',
    category: 'Compliance',
    permission: 'feature.ofac-compliance.use',
  },
  {
    key: 'website-compliance',
    name: 'Website Compliance',
    description: 'Public-site crawl and email authentication checks. Does not open the cloud account behind the site.',
    category: 'Compliance',
    permission: 'feature.website-compliance.use',
  },
  {
    key: 'cloud-compliance',
    name: 'Cloud Compliance',
    description: 'Written cloud checklist on CompliCore. CompliAdvantage adds a once-a-day read-only review of the client account.',
    category: 'Compliance',
    permission: 'feature.cloud-compliance.use',
  },
  {
    key: 'dod-contracts-compliance',
    name: 'Government Compliance',
    description: 'Federal, state, and local vendor readiness including SAM and CMMC Level 1 trackers.',
    category: 'Compliance',
    permission: 'feature.dod-contracts-compliance.use',
  },
  {
    key: 'education-compliance',
    name: 'Education Compliance',
    description: 'Compliance board for education and campus operators.',
    category: 'Compliance',
    permission: 'feature.education-compliance.use',
  },
  {
    key: 'ai-compliance',
    name: 'AI Compliance',
    description: 'Inventory, acceptable-use rule, and human review for AI tools. Does not certify NIST AI RMF or ISO/IEC 42001.',
    category: 'Compliance',
    permission: 'feature.ai-compliance.use',
  },
  {
    key: 'business-creation-compliance',
    name: 'Legal Entity Formation',
    description: 'Limited liability company and formation checklist on CompliCore.',
    category: 'Compliance',
    permission: 'feature.business-creation-compliance.use',
  },
  {
    key: 'wills-trusts-compliance',
    name: 'Wills & Trusts',
    description: 'One-question interview that becomes a will or revocable-trust checklist. NextCompli does not write or file the papers.',
    category: 'Compliance',
    permission: 'feature.wills-trusts-compliance.use',
  },
  {
    key: 'dashboards',
    name: 'Dashboards',
    description: 'Operator dashboards with data-source health monitoring.',
    category: 'Platform',
    permission: 'feature.dashboards.use',
  },
  {
    key: 'research-reports',
    name: 'Research reports (Vantage)',
    description: 'Person and company research jobs and PDF reports.',
    category: 'Research',
    permission: 'feature.research-reports.use',
  },
  {
    key: 'continuous-monitoring',
    name: 'Continuous monitoring',
    description: 'Automatic rescreen when Treasury list notices refresh the book.',
    category: 'Monitoring',
    permission: 'feature.continuous-monitoring.use',
  },
  {
    key: 'real-time-batch-screening',
    name: 'Real-time + batch screening',
    description: 'CSV and payment-party batch checks before funds move.',
    category: 'Monitoring',
    permission: 'feature.real-time-batch-screening.use',
  },
  {
    key: 'audit-trail-alert-workflow',
    name: 'Audit trail + alert workflow',
    description: 'Owned alert pipelines with an auditable trail.',
    category: 'Monitoring',
    permission: 'feature.audit-trail-alert-workflow.use',
  },
  {
    key: 'real-time-alerts',
    name: 'Real-time alerts',
    description: 'Priority incident alerts for the monitoring book.',
    category: 'Monitoring',
    permission: 'feature.real-time-alerts.use',
  },
  {
    key: 'embedded-consultants',
    name: 'Embedded consultants',
    description: 'Advisory support for rollout and operating model.',
    category: 'Governance',
    permission: 'feature.embedded-consultants.use',
  },
  {
    key: 'executive-compliance-reporting',
    name: 'Executive compliance reporting',
    description: 'Board-ready compliance summaries.',
    category: 'Governance',
    permission: 'feature.executive-compliance-reporting.use',
  },
  {
    key: 'scheduled-audits',
    name: 'Scheduled audits',
    description: 'Recurring audit evidence collection.',
    category: 'Governance',
    permission: 'feature.scheduled-audits.use',
  },
  {
    key: 'case-audit-workspace',
    name: 'Case and audit workspace',
    description: 'Case file workspace for audits and governance evidence.',
    category: 'Governance',
    permission: 'feature.case-audit-workspace.use',
  },
];

export const FEATURE_CATALOG_BY_KEY: Record<string, FeatureCatalogEntry> = FEATURE_CATALOG.reduce(
  (acc, entry) => {
    acc[entry.key] = entry;
    return acc;
  },
  {} as Record<string, FeatureCatalogEntry>,
);

export const FEATURE_CATEGORIES = Array.from(
  new Set(FEATURE_CATALOG.map((entry) => entry.category)),
);

export function featureLabel(key: string): string {
  return FEATURE_CATALOG_BY_KEY[key]?.name || key;
}
